import { useCallback, useState } from 'react';
import styles from './EditScenarioDetails.module.css';
import { ScenarioDefinitionType } from '../../api/scenarioDefinitions';
import ScenarioEditableField from './ScenarioEditableField';
import ScenarioEditableImage from './ScenarioEditableImage';
import { visibilityPublicIcon, visibilityPrivateIcon, visibilityUnlistedIcon } from '../../assets/icons';

const CHAR_LIMITS = {
  display_name: 20,
  tagline: 50,
  description: 4000
} as const;

interface EditScenarioDetailsProps {
  definition: ScenarioDefinitionType;
  onUpdate?: (updatedDefinition: ScenarioDefinitionType) => void;
}

function EditScenarioDetails({ definition, onUpdate }: EditScenarioDetailsProps): JSX.Element {
  const [notification, setNotification] = useState<{ message: string, type: string } | null>(null);

  const handleUpdateField = useCallback(async (field: 'display_name' | 'tagline' | 'description' | 'visibility', value: string | boolean) => {
    try {
      const updatedDefinition = await definition.update({ [field]: value });
      onUpdate?.(updatedDefinition);
      
      // Show notification when visibility is updated
      if (field === 'visibility') {
        const visibilityLabels = {
          public: 'Public',
          unlisted: 'Unlisted',
          private: 'Private'
        };
        
        setNotification({
          message: `Visibility changed to ${visibilityLabels[value as keyof typeof visibilityLabels]}`,
          type: 'success'
        });
        
        // Hide notification after 3 seconds
        setTimeout(() => {
          setNotification(null);
        }, 3000);
      }
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
      setNotification({
        message: `Failed to update ${field}`,
        type: 'error'
      });
      
      // Hide error notification after 3 seconds
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    }
  }, [definition, onUpdate]);

  const handleIconUpdate = useCallback(async (file: File) => {
    try {
      const updatedDefinition = await definition.update({ icon: file });
      onUpdate?.(updatedDefinition);
    } catch (error) {
      console.error('Failed to update icon:', error);
      setNotification({
        message: 'Failed to update icon',
        type: 'error'
      });
      
      // Hide error notification after 3 seconds
      setTimeout(() => {
        setNotification(null);
      }, 3000);
    }
  }, [definition, onUpdate]);

  // Get the appropriate icon based on visibility
  const getVisibilityIcon = () => {
    switch (definition.visibility) {
      case 'public':
        return visibilityPublicIcon;
      case 'unlisted':
        return visibilityUnlistedIcon;
      case 'private':
        return visibilityPrivateIcon;
      default:
        return visibilityPublicIcon;
    }
  };

  return (
    <>
      {notification && (
        <div className={`${styles.notification} ${styles[notification.type]}`}>
          {notification.type === 'success' && (
            <img src={getVisibilityIcon()} alt="Visibility" className={styles.notificationIcon} />
          )}
          {notification.message}
        </div>
      )}
      
      <div className={styles.scenarioImageSection}>
        <ScenarioEditableImage
          icon={definition.icon}
          altText={definition.display_name}
          onUpdate={handleIconUpdate}
        />
      </div>
      
      <div className={styles.scenarioDetailsSection}>
        <div className={styles.scenarioHeader}>
          <div className={styles.editableFieldContainer}>
            <div className={styles.fieldLabel}>Title</div>
            <ScenarioEditableField
              value={definition.display_name}
              onSave={(value) => handleUpdateField('display_name', value)}
              label="Title"
              className={styles.titleEdit}
              maxLength={CHAR_LIMITS.display_name}
              renderDisplay={(value) => (
                <h1 className={styles.scenarioTitle}>{value}</h1>
              )}
            />
          </div>
          
          <div className={styles.editableFieldContainer}>
            <div className={styles.fieldLabel}>Tagline</div>
            <ScenarioEditableField
              value={definition.tagline || ''}
              onSave={(value) => handleUpdateField('tagline', value)}
              label="Tagline"
              className={styles.taglineEdit}
              maxLength={CHAR_LIMITS.tagline}
              renderDisplay={(value) => (
                value ? <p className={styles.scenarioTagline}>{value}</p> : 
                <p className={styles.scenarioTaglinePlaceholder}>Add a tagline...</p>
              )}
            />
          </div>
          
          <div className={styles.editableFieldContainer}>
            <div className={styles.fieldLabel}>Description</div>
            <ScenarioEditableField
              value={definition.description || ''}
              onSave={(value) => handleUpdateField('description', value)}
              label="Description"
              className={styles.descriptionEdit}
              maxLength={CHAR_LIMITS.description}
              renderDisplay={(value) => (
                <div className={styles.scenarioDescription}>
                  {value ? (
                    <p>
                      {value.split(/({{user}}|{{char}})/g).map((part, index) => {
                        if (part === '{{user}}') {
                          return <span key={index} className={styles.highlightedUserToken}>{part}</span>;
                        }
                        if (part === '{{char}}') {
                          return <span key={index} className={styles.highlightedCharToken}>{part}</span>;
                        }
                        return <span key={index}>{part}</span>;
                      })}
                    </p>
                  ) : (
                    <p className={styles.scenarioDescriptionPlaceholder}>Add a description...</p>
                  )}
                </div>
              )}
            />
          </div>
          
          <div className={styles.editableFieldContainer}>
            <div className={styles.fieldLabel}>Visibility</div>
            <div className={styles.visibilitySelector}>
              <div className={styles.visibilityWrapper}>
                <span className={styles.visibilityIcon}>
                  <img src={getVisibilityIcon()} alt="Visibility" />
                </span>
                <select
                  id="visibility"
                  name="visibility"
                  value={definition.visibility || 'public'}
                  onChange={(e) => handleUpdateField('visibility', e.target.value)}
                  className={styles.visibilitySelect}
                >
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditScenarioDetails; 